import { useAtom } from "jotai";
import React from "react";
import moment from "moment";

// lucide icons
import {
    Move,
    CalendarX,
    PauseCircle,
    Bus,
    Plus,
    Minus,
    Repeat,
    BadgeX,
    UserMinus,
    PlayCircle,
    Baby,
    Activity,
    Calendar,
    FileText,
    LogOut,
    DollarSign
} from "lucide-react";

import { activeChildDetail, alertModal, selectedSupportItem } from "../../atomstore/store";
import { useRequestProcessor } from "../../requestProcessor";
import { cancelPauseRequest, cancelRequest } from "../../queryfunctions/queryfunctions";
import { DEFAULT_FORMAT } from "../common/FootprintDatePicker";
import SpinnerLoading from "../common/SpinnerLoading";
import { useNavigate } from "react-router-dom";

const PastRequestItem = ({ item, refetch, setLoading }) => {
    const [, setSelectedSupportItem] = useAtom(selectedSupportItem);
    const [, setModal] = useAtom(alertModal);
    const [activeChild] = useAtom(activeChildDetail);
    const { useMutationWrapper } = useRequestProcessor();
    const navigate = useNavigate();
    const isRestrictionActive = moment().isSameOrAfter("2026-04-01", "day");



    const iconGradients = {
        "Center Shift": "from-blue-400 to-blue-600",
        Leaves: "from-purple-400 to-purple-600",
        "Child Pause": "from-indigo-400 to-indigo-600",
        "Add One Way Transport": "from-green-400 to-green-600",
        "Add Two Way Transport": "from-green-400 to-green-600",
        "Delete One Way Transport": "from-red-400 to-red-600",
        "Delete Two Way Transport": "from-red-400 to-red-600",
        "Program Change": "from-orange-400 to-orange-600",
        "Not Satisfied Refund": "from-amber-400 to-amber-600",
        "Child Attrition": "from-rose-400 to-rose-600",
        "Child Resume": "from-emerald-400 to-emerald-600",
        "Extended Daycare": "from-teal-400 to-teal-600",
    };


    // ---- ICON WRAPPER ----
    const IconWrapper = ({ children, gradient }) => (
        <div
            className={`size-14 rounded-xl p-3 flex items-center justify-center 
        bg-gradient-to-br ${gradient} shadow-sm text-white`}
        >
            {children}
        </div>
    );

    const icons = {
        "Center Shift": (
            <IconWrapper gradient={iconGradients["Center Shift"]}>
                <Activity size={30} />
            </IconWrapper>
        ),

        Leaves: (
            <IconWrapper gradient={iconGradients["Leaves"]}>
                <Calendar size={30} />
            </IconWrapper>
        ),

        "Child Pause": (
            <IconWrapper gradient={iconGradients["Child Pause"]}>
                <Calendar size={30} />
            </IconWrapper>
        ),

        "Add One Way Transport": (
            <IconWrapper gradient={iconGradients["Add One Way Transport"]}>
                <Bus size={30} />
            </IconWrapper>
        ),

        "Add Two Way Transport": (
            <IconWrapper gradient={iconGradients["Add Two Way Transport"]}>
                <Bus size={30} />
            </IconWrapper>
        ),

        "Delete One Way Transport": (
            <IconWrapper gradient={iconGradients["Delete One Way Transport"]}>
                <Bus size={30} />
            </IconWrapper>
        ),

        "Delete Two Way Transport": (
            <IconWrapper gradient={iconGradients["Delete Two Way Transport"]}>
                <Bus size={30} />
            </IconWrapper>
        ),

        "Program Change": (
            <IconWrapper gradient={iconGradients["Program Change"]}>
                <FileText size={30} />
            </IconWrapper>
        ),

        "Not Satisfied Refund": (
            <IconWrapper gradient={iconGradients["Not Satisfied Refund"]}>
                <DollarSign size={30} />
            </IconWrapper>
        ),

        "Child Attrition": (
            <IconWrapper gradient={iconGradients["Child Attrition"]}>
                <LogOut size={30} />
            </IconWrapper>
        ),

        "Child Resume": (
            <IconWrapper gradient={iconGradients["Child Resume"]}>
                <PlayCircle size={30} />
            </IconWrapper>
        ),

        "Extended Daycare": (
            <IconWrapper gradient={iconGradients["Extended Daycare"]}>
                <Baby size={30} />
            </IconWrapper>
        ),
    };

    // STATUS GRADIENT
    const statusGradients = {
        Pending: "from-yellow-400 to-yellow-600",
        Processing: "from-yellow-400 to-yellow-600",
        Approved: "from-green-400 to-green-600",
        Cancelled: "from-red-400 to-red-600",
    };

    const StatusBadge = ({ status }) => (
        <span
            className={`text-[11px] px-2 py-1 rounded-md text-white font-semibold bg-gradient-to-r ${statusGradients[status]}`}
        >
            {status}
        </span>
    );

    // ---- MUTATIONS ----
    const CancelPause = useMutationWrapper(
        "CancelPause",
        (item) => cancelPauseRequest(item.request_id),
        {
            onSuccess: () => {
                setLoading(false)
                refetch()
            }
        }
    );

    const CancelRequest = useMutationWrapper(
        "CancelRequest",
        (item) => cancelRequest(item.request_id),
        {
            onSuccess: () => {
                setLoading(false)
                refetch()
            }
        }
    );

    const onCancelPress = (item) => {
        console.log("yes")
        setModal((prev) => ({
            ...prev,
            state: true,
            type: "confirm",
            isConfirm: true,
            message: "Are You Sure You Want To Cancel This Request ?",
            onOkPressed: () => {
                setLoading(true);
                if (item.type === "Leaves" || item.type === "Child Pause") {
                    CancelPause.mutate(item);
                } else {
                    CancelRequest.mutate(item);
                }
            },
            onCancelPressed: () => { },
        }));
    };

    return (
        <li className=" bg-white rounded-xl shadow overflow-hidden">

            {/* {(CancelRequest.isLoading || CancelPause.isLoading) && <SpinnerLoading />} */}

            <div className="grid grid-cols-12 gap-7 p-3">

                {/* LEFT ICON */}
                <div className="col-span-2 md:col-span-1 flex justify-start">
                    {icons[item.type]}
                </div>

                {/* RIGHT CONTENT */}
                <div className="col-span-10 md:col-span-11 flex flex-col">

                    {/* Top Row: Type + Status */}
                    <div className="flex justify-between items-start">

                        <p className="text-[15px] font-semibold">{item?.type}
                            <br />
                            <span className="text-[12px] text-gray-700 mt-2">{moment(item?.date).format(DEFAULT_FORMAT)}</span>
                        </p>

                        {/* Status */}
                        <div className="flex flex-col items-end">
                            <StatusBadge status={item?.status} />

                            {/* Request Date UNDER Status */}
                            <p className="text-[11px] text-gray-500 mt-1">
                                {moment(item?.request_date).fromNow()}
                            </p>

                        </div>
                    </div>

                    {/* Affected Date */}
                    <p className="text-[12px] text-gray-700 mt-2">

                    </p>


                </div>
            </div>

            {/* FULL-WIDTH CANCEL BAR */}
            {item.showCancel && (
                <button
                    onClick={() => onCancelPress(item)}
                    className="w-full py-3 text-center text-white font-semibold text-sm 
                               bg-gradient-to-r from-cyan-500 to-cyan-700 active:opacity-80">
                    Cancel Request
                </button>
            )}

            {/* EARLY PAUSE / RESUME BUTTONS */}
            {item.showEarlyPauseAndResume && (
                <div className="flex w-full">
                    {/* EARLY RESUME */}
                    <button
                        onClick={() => {
                            setSelectedSupportItem(item);
                            navigate('/early-resume');
                        }}
                        className={`w-1/2 py-3 text-center text-white font-semibold text-sm 
                       bg-gradient-to-r from-orange-500 to-orange-600 active:opacity-80`}
                    >
                        Early Resume
                    </button>

                    {/* EXTEND PAUSE */}
                    <button
                        onClick={() => {
                            setSelectedSupportItem(item);
                            navigate('/extend-pause');
                        }}
                        className="w-1/2 py-3 text-center text-white font-semibold text-sm 
                       bg-gradient-to-r from-purple-500 to-purple-600 active:opacity-80"
                    >
                        Extend Pause
                    </button>
                </div>
            )}

        </li>
    );
};

export default PastRequestItem;
